import React, { useState } from "react";
import {
  Button,
  Pagination,
  IconButton,
  Message,
  Table,
  Loader,
  useToaster,
  InputGroup,
  Input,
} from "rsuite";
import { queryPostRequest } from "../../api";
import "./QueryHistoryPanel.css";
import CopyIcon from "@rsuite/icons/Copy";
import CheckIcon from "@rsuite/icons/Check";
import CloseIcon from "@rsuite/icons/Close";
import TrashIcon from "@rsuite/icons/Trash";
import SearchIcon from "@rsuite/icons/Search";
import { CodeBlock } from "@atlaskit/code";
import { useTheme } from "../../context/ThemeContext";

const { Column, HeaderCell, Cell } = Table;

const QueryHistoryPanel = ({
  userId,
  queryHistory,
  setQueryHistory,
  setQueryHistoryOpen,
  setActiveIconRight,
  queryHistoryLoading,
}) => {
  const { isDarkMode } = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const toaster = useToaster();

  React.useEffect(() => {
    if (!userId) return;

    let isCancelled = false;

    const fetchLatest = async () => {
      try {
        const data = await queryPostRequest("get_query_history", {
          user_id: userId,
        });
        if (!isCancelled) {
          setQueryHistory(data.query_history || []);
        }
      } catch (error) {
        console.error("Failed to refresh query history", error);
      }
    };

    fetchLatest();
    const intervalId = setInterval(fetchLatest, 5000);

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
    };
  }, [userId, setQueryHistory]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, rowsPerPage, queryHistory.length]);

  const paginatedData = queryHistory
    .filter((item) =>
      item.query.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => console.log("Copied to clipboard"))
      .catch((err) => console.error("Failed to copy: ", err));
  };

  return (
    <div className="query-history-panel">
      <div className="query-history-container">
        <div className="query-history-header">
          <div className="query-history-title-container">
            <h5 className="query-history-title">Query History</h5>
            <InputGroup className="search-bar">
              <Input
                placeholder="Search by Query"
                value={searchQuery}
                onChange={(value) => setSearchQuery(value)}
                autoComplete="off"
              />
              <InputGroup.Addon>
                <SearchIcon />
              </InputGroup.Addon>
            </InputGroup>
            <IconButton
              icon={<TrashIcon />}
              className="delete-all-button"
              appearance="primary"
              onClick={async () => {
                const requestBody = {
                  user_id: userId,
                };

                try {
                  await queryPostRequest(
                    "delete_query_history_all",
                    requestBody
                  );
                  setQueryHistory([]);
                  toaster.push(
                    <Message type="success" showIcon closable>
                      Cleared all query history successfully!
                    </Message>,
                    { placement: "topCenter", duration: 3000 }
                  );
                } catch (error) {
                  console.error("Failed to clear query history", error);
                  toaster.push(
                    <Message type="error" showIcon closable>
                      Error! Could not clear query history.
                    </Message>,
                    { placement: "topCenter", duration: 3000 }
                  );
                }
              }}
            >
              Clear History
            </IconButton>
          </div>
        </div>
        <div className="query-history-body">
          {queryHistoryLoading ? (
            <Loader center />
          ) : queryHistory.length > 0 ? (
            <>
              <div className="query-history-table-container">
                <Table
                  data={paginatedData}
                  fillHeight
                  className="query-history-table"
                  rowHeight={110}
                  rowClassName={(rowData) => {
                    if (!rowData) return "";
                    return rowData.response === "200"
                      ? "success-row"
                      : "error-row";
                  }}
                  hover={false}
                >
                  <Column
                    width={100}
                    align="center"
                    verticalAlign="middle"
                    fixed
                  >
                    <HeaderCell>Status</HeaderCell>
                    <Cell>
                      {(rowData) =>
                        rowData.response === "200" ? (
                          <CheckIcon
                            color={isDarkMode ? "#5ee281" : "green"}
                            className="table-icon"
                          />
                        ) : (
                          <CloseIcon 
                          color={isDarkMode ? "#F61313" : "red"} 
                          className="table-icon" />
                        )
                      }
                    </Cell>
                  </Column>
                  <Column width={175} align="center">
                    <HeaderCell>Time</HeaderCell>
                    <Cell>
                      {(rowData) => new Date(rowData.timestamp).toISOString()}
                    </Cell>
                  </Column>

                  <Column width={100} align="center">
                    <HeaderCell>Datasource</HeaderCell>
                    <Cell dataKey="datasource"></Cell>
                  </Column>

                  <Column width={80} align="center">
                    <HeaderCell>Duration</HeaderCell>
                    <Cell dataKey="duration" />
                  </Column>

                  <Column width={70} align="center">
                    <HeaderCell>Rows</HeaderCell>
                    <Cell dataKey="rows" />
                  </Column>

                  <Column width={100} align="center">
                    <HeaderCell>Size</HeaderCell>
                    <Cell dataKey="size" />
                  </Column>

                  <Column width={270} align="left">
                    <HeaderCell>Query</HeaderCell>
                    <Cell>
                      {(rowData) => (
                        <div className="code-container">
                          <div className="copy-button-wrapper">
                            <IconButton
                              appearance="subtle"
                              icon={<CopyIcon />}
                              onClick={() => copyToClipboard(rowData.query)}
                            />
                          </div>
                          <CodeBlock
                            shouldWrapLongLines
                            text={rowData.query}
                            language="sql"
                            showLineNumbers={false}
                            className="query-history-code-block"
                          />
                        </div>
                      )}
                    </Cell>
                  </Column>
                  <Column width={100} align="center" verticalAlign="middle">
                    <HeaderCell>Actions</HeaderCell>
                    <Cell>
                      {(rowData) => (
                        <TrashIcon
                          className="table-icon"
                          onClick={async () => {
                            const requestBody = {
                              user_id: userId,
                              id: rowData._id,
                            };

                            try {
                              await queryPostRequest(
                                "delete_query_history",
                                requestBody
                              );
                              setQueryHistory((prevHistory) =>
                                prevHistory.filter(
                                  (query) => query._id !== rowData._id
                                )
                              );
                              toaster.push(
                                <Message type="success" showIcon closable>
                                  Deleted query successfully!
                                </Message>,
                                { placement: "topCenter", duration: 3000 }
                              );
                            } catch (error) {
                              console.error(
                                "Failed to delete query history",
                                error
                              );
                              toaster.push(
                                <Message type="error" showIcon closable>
                                  Error! Could not delete query.
                                </Message>,
                                { placement: "topCenter", duration: 3000 }
                              );
                            }
                          }}
                        />
                      )}
                    </Cell>
                  </Column>
                </Table>
              </div>
              <div className="pagination-container">
                <Pagination
                  prev
                  next
                  ellipsis
                  boundaryLinks
                  maxButtons={5}
                  size="xs"
                  layout={["total", "-", "|", "pager"]}
                  total={
                    queryHistory.filter((item) =>
                      item.query
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase())
                    ).length
                  }
                  limitOptions={[10, 20, 30, 50]}
                  limit={rowsPerPage}
                  activePage={currentPage}
                  onChangePage={setCurrentPage}
                  onChangeLimit={(value) => {
                    setRowsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </>
          ) : (
            <div className="no-query-history-error">
              <Message showIcon type="error">
                <strong>No query history available</strong>
              </Message>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default QueryHistoryPanel;